import { useTranslation } from "react-i18next";
import { useForm, useWatch } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  latitudeSchema,
  longitudeSchema,
  type CreateSiteRequest,
  type Site,
} from "@/contracts";
import { z } from "zod";

import { createSiteMutation, organizationOptionsQuery } from "@/api/queries";
import { useAuth } from "@/auth/auth-context";
import { Alert } from "@/components/ui/alert";
import { SelectField, TextField } from "@/components/ui/field";
import { FormDialog } from "@/components/ui/form-dialog";
import { useApiClient } from "@/lib/api-context";
import { ApiError } from "@/lib/api-error";

/**
 * Coordinates arrive as text, because an empty number input yields `""` and
 * `Number("")` is 0 — which would drop a site in the Gulf of Guinea rather
 * than leaving it unplaced.
 */
const optionalCoordinate = z
  .string()
  .trim()
  .refine((value) => value === "" || Number.isFinite(Number(value)), {
    message: "Enter a number, or leave it empty.",
  });

/** Kept separate from the refinements below, which would hide `.shape`. */
const siteFormFields = z.object({
  organizationId: z.string(),
  name: z.string().trim().min(2, "Enter a name of at least 2 characters.").max(120),
  address: z.string().trim().max(240),
  latitude: optionalCoordinate,
  longitude: optionalCoordinate,
});

const siteFormSchema = siteFormFields
  // Half a coordinate pair points at nothing, so the API rejects it. Catching
  // it here means the user is told before a round trip.
  .refine((value) => (value.latitude === "") === (value.longitude === ""), {
    message: "Give both coordinates, or neither.",
    path: ["longitude"],
  })
  .refine(
    (value) => value.latitude === "" || latitudeSchema.safeParse(Number(value.latitude)).success,
    { message: "Latitude runs from -90 to 90.", path: ["latitude"] },
  )
  .refine(
    (value) => value.longitude === "" || longitudeSchema.safeParse(Number(value.longitude)).success,
    { message: "Longitude runs from -180 to 180.", path: ["longitude"] },
  );

type SiteFormValues = z.infer<typeof siteFormSchema>;

export function SiteCreateDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  /** Called with the new site before the dialog closes (e.g. wizard selection). */
  onCreated?: (site: Site) => void;
}) {
  const client = useApiClient();
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // A super admin belongs to no tenant, so the API makes them name one.
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const organizations = useQuery({ ...organizationOptionsQuery(client), enabled: isSuperAdmin });

  const {
    register,
    handleSubmit,
    reset,
    setError,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<SiteFormValues>({
    resolver: zodResolver(siteFormSchema),
    defaultValues: { organizationId: "", name: "", address: "", latitude: "", longitude: "" },
  });

  const organizationId = useWatch({ control, name: "organizationId" });

  const mutation = useMutation({
    ...createSiteMutation(client),
    onSuccess: async () => {
      // The whole family: a new site changes the list, the pickers on the
      // device forms and the counts a dashboard may already hold.
      await queryClient.invalidateQueries({ queryKey: ["sites"] });
    },
  });

  function close() {
    reset();
    mutation.reset();
    onClose();
  }

  const onSubmit = handleSubmit(async (values) => {
    if (isSuperAdmin && !values.organizationId) {
      setError("organizationId", { message: t("chooseOrgForSite") });

      return;
    }

    const body: CreateSiteRequest = {
      name: values.name,
      ...(values.address ? { address: values.address } : {}),
      ...(values.latitude ? { latitude: Number(values.latitude) } : {}),
      ...(values.longitude ? { longitude: Number(values.longitude) } : {}),
      ...(values.organizationId ? { organizationId: values.organizationId } : {}),
    };

    try {
      const created = await mutation.mutateAsync(body);
      onCreated?.(created);
      close();
    } catch (error) {
      if (error instanceof ApiError) {
        for (const [field, message] of Object.entries(error.fieldErrors())) {
          if (field in siteFormFields.shape) {
            setError(field as keyof SiteFormValues, { message });
          }
        }
      }
    }
  });

  return (
    <FormDialog
      open={open}
      title={t("dialogAddSiteTitle")}
      description={t("dialogAddSiteDescription")}
      submitLabel={t("createSite")}
      pending={isSubmitting || mutation.isPending}
      onSubmit={(event) => void onSubmit(event)}
      onCancel={close}
    >
      <div className="flex flex-col gap-4">
        {mutation.isError ? <Alert>{mutation.error.message}</Alert> : null}

        {isSuperAdmin ? (
          <SelectField
            label={tCommon("organization")}
            required
            {...(errors.organizationId?.message === undefined
              ? {}
              : { error: errors.organizationId.message })}
            value={organizationId}
            onChange={(value) =>
              setValue("organizationId", value, { shouldValidate: true, shouldDirty: true })
            }
            data={[
              { value: "", label: t("chooseOrganization") },
              ...(organizations.data?.data ?? []).map((organization) => ({
                value: organization.id,
                label: organization.name,
              })),
            ]}
          />
        ) : null}

        <TextField
          label={tCommon("name")}
          required
          placeholder={t("placeholderSiteName")}
          {...(errors.name?.message === undefined ? {} : { error: errors.name.message })}
          {...register("name")}
        />

        <TextField
          label={tCommon("address")}
          hint={t("optionalHint")}
          {...(errors.address?.message === undefined ? {} : { error: errors.address.message })}
          {...register("address")}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label={t("fieldLatitude")}
            inputMode="decimal"
            placeholder="-31.4283"
            hint={t("latitudeHint")}
            {...(errors.latitude?.message === undefined ? {} : { error: errors.latitude.message })}
            {...register("latitude")}
          />

          <TextField
            label={t("fieldLongitude")}
            inputMode="decimal"
            placeholder="-64.1888"
            {...(errors.longitude?.message === undefined
              ? {}
              : { error: errors.longitude.message })}
            {...register("longitude")}
          />
        </div>
      </div>
    </FormDialog>
  );
}
